import React, { useState, useImperativeHandle, forwardRef } from "react";
import { Pencil, Check, Trash2 } from "lucide-react";
import PackageModal from "../components/modal/packageModal";
import { usePackageStore } from "../hooks/plans/addplans";
import { usePlanUpdate } from "../hooks/plans/update";
import { useRemovePlan } from "../hooks/plans/deleteplan";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import { hydratePlanLimits } from "../utils/planLimitsForm";


const Packages = forwardRef((props, ref) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("edit");
  const planDeleteMutation = useRemovePlan();
  const [openMenuId, setOpenMenuId] = React.useState(null);

  const createEmptyForm = () => ({
    name: "",
    description: "",
    code: "",
    type: "",
    billingCycle: "monthly",
    pricingType: "company",
    price: "",
    offerPrice: "",
    limits: hydratePlanLimits({}),
    features: [""],
  });

  const [formData, setFormData] = useState(createEmptyForm);


  const subcripPlans = props.data || [];
  const addPackageMutation = usePackageStore();
  const updatePackageMutation = usePlanUpdate();
  const [editingPlanId, setEditingPlanId] = useState(null);

  const mapPlanToForm = (plan) => {
    const pricing = plan.pricing?.[0] || {};
    const planType =
      plan.type === "free" ? "trial" : plan.type === "trial" || plan.type === "paid" ? plan.type : "";
    const pricingType =
      pricing.type === "user" || pricing.type === "company"
        ? pricing.type
        : "company";
    const billingCycle =
      pricing.billingCycle === "yearly" || pricing.billingCycle === "monthly"
        ? pricing.billingCycle
        : "monthly";

    return {
      name: plan.name || "",
      description: plan.description || "",
      code: plan.code || "",
      type: planType,
      billingCycle,
      pricingType,
      price: pricing.price ?? "",
      offerPrice: pricing.offerPrice ?? "",
      limits: hydratePlanLimits(plan.limits),
      features:
        (plan.features || []).filter((f) => typeof f === "string").length > 0
          ? (plan.features || []).filter((f) => typeof f === "string")
          : [""],
    };
  };

  const handleEdit = (plan) => {
    const planId = plan._id || plan.id;
    setModalMode("edit");
    setEditingPlanId(planId);
    setFormData(mapPlanToForm(plan));
    setIsModalOpen(true);
  };

  const handleDelete = (planId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "This action cannot be undone.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        planDeleteMutation.mutate(planId, {
          onSuccess: () => {
            toast.success("Plan deleted successfully");
            if (props.onRefresh) props.onRefresh();
          },
          onError: (err) => {
            toast.error("Failed to delete plan");
            console.error(err);
          }
        });
      }
    });
    setOpenMenuId(null);
  };
  const handleAddPackage = () => {
    if (ref.current) {
      ref.current.openAddModal();
    }
  };

  const handleAddNew = () => {
    setModalMode("add");
    setFormData(createEmptyForm());
    setEditingPlanId(null);
    setIsModalOpen(true);
  };

  useImperativeHandle(ref, () => ({
    openAddModal: handleAddNew,
  }));

  const resetForm = () => {
    setFormData(createEmptyForm());
    setEditingPlanId(null);
  };

  const handleSave = (payload) => {
    if (modalMode === "add") {
      addPackageMutation.mutate(payload, {
        onSuccess: () => {
          toast.success("Plan created successfully");
          setIsModalOpen(false);
          resetForm();
          if (props.onRefresh) props.onRefresh();
        },
        onError: (err) => {
          toast.error(err.response?.data?.message || "Failed to create plan");
          console.error("Failed to create package:", err);
        },
      });
    } else if (modalMode === "edit" && editingPlanId) {
      updatePackageMutation.mutate(
        { id: editingPlanId, body: payload },
        {
          onSuccess: () => {
            toast.success("Plan updated successfully");
            setIsModalOpen(false);
            resetForm();
            if (props.onRefresh) props.onRefresh();
          },
          onError: (err) => {
            toast.error(err.response?.data?.message || "Failed to update plan");
            console.error("Failed to update package:", err);
          },
        }
      );
    }
  };

  return (
    <>
      <div className="subscription-cards-container">
        {subcripPlans.map((plan, index) => (
          <div key={index} className="subscrip-cards" style={{ position: 'relative' }}>
            {plan.code && (
              <div style={{
                position: 'absolute',
                top: '-16px',
                left: 0,
                right: 0,
                zIndex: 10,
                background: 'transparent',
              }}>
                <span className="plan-code-badge">
                  {plan.code}
                </span>
              </div>
            )}
            <div className="contain-head" style={{ marginTop: '15px' }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <div
                  className={plan.type === "paid" ? "type-color" : "type-free"}
                >
                  {plan.type === "paid" ? "Paid" : plan.type === "trial" ? "Trial" : plan.type || "—"}
                </div>

                {plan.isActive ? (
                  <button className="action-button">Active</button>
                ) : (
                  <button className="action-button-inactive">Inactive</button>
                )}
              </div>
              <div className="icons">

                <Pencil
                  size={16}
                  style={{
                    color: "#5C308D",
                    marginLeft: "8px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleEdit(plan, index)}
                />
                <Trash2
                  size={16}
                  style={{
                    color: "#dc3545",
                    marginLeft: "8px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleDelete(plan._id || plan.id)}
                />
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                margin: "20px 0",
                justifyContent: "space-between",
              }}
            >
              <h3 style={{ fontSize: "14px", fontWeight: "600" }}>
                {plan.name}
              </h3>
            </div>

            <p
              style={{ fontSize: "24px", fontWeight: "600", color: "#5C308D" }}
            >
              {Array.isArray(plan.pricing) &&
                plan.pricing[0] &&
                plan.pricing[0].offerPrice ? (
                <>
                  <span
                    style={{
                      fontSize: "24px",
                      fontWeight: "600",
                      color: "#5C308D",
                      marginRight: "8px",
                    }}
                  >
                    AED {plan.pricing[0].offerPrice}
                  </span>
                  <span
                    style={{
                      fontSize: "18px",
                      fontWeight: "400",
                      color: "#888",
                      textDecoration: "line-through",
                    }}
                  >
                    AED {plan.pricing[0].price}
                  </span>
                </>
              ) : (
                <span
                  style={{
                    fontSize: "24px",
                    fontWeight: "600",
                    color: "#5C308D",
                  }}
                >
                  AED 
                  {Array.isArray(plan.pricing) && plan.pricing[0]
                    ? plan.pricing[0].price
                    : ""}
                </span>
              )}
              {plan.pricing && plan.pricing[0]?.billingCycle && (
                <span style={{ fontSize: '13px', fontWeight: 500, color: '#5C308D', marginLeft: '2px' }}>
                  /{plan.pricing[0].billingCycle}
                </span>
              )}
            </p>

            <p
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: "#222222b6",
                margin: "5px 0",
              }}
            >
              {plan.description}
            </p>

            <div
              style={{
                margin: "15px 0",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              {plan.features &&
                plan.features.map((feature, idx) => (
                  <span
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      fontSize: "12px",
                      color: "#222222b6",
                      margin: "0",
                      gap: "4px",
                      lineHeight: "20px",

                    }}
                  >
                    <Check size={18} className="check-icon" /> {feature}
                  </span>
                ))}
            </div>

            <footer style={{ borderTop: "1px solid #e0e0e0" }}>
              <div className="footer-sect">
                <h6 className="company-email" style={{ margin: "10px 0" }}>
                  Users Limit
                </h6>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#222222e2",
                    fontWeight: "600",
                    margin: "0",
                  }}
                >
                  {plan.limits?.maxUsers ?? "—"}
                </p>
              </div>
              <div className="footer-sect">
                <h6 className="company-email" style={{ margin: "10px 0" }}>
                  Products Limit
                </h6>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#222222e2",
                    fontWeight: "600",
                    margin: "0",
                  }}
                >
                  {plan.limits?.maxProducts ?? "—"}
                </p>
              </div>
              <div className="footer-sect">
                <h6 className="company-email" style={{ margin: "10px 0" }}>
                  Storage
                </h6>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#222222e2",
                    fontWeight: "600",
                    margin: "0",
                  }}
                >
                  {plan.limits?.storageSpaceInGB ?? "—"} GB
                </p>
              </div>
            </footer>
            <div>
            </div>
          </div>
        ))}
      </div>
      <PackageModal
        key={`${modalMode}-${editingPlanId || "new"}`}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        formData={formData}
        setFormData={setFormData}
        mode={modalMode}
        onSave={handleSave}
        isLoading={
          modalMode === "add"
            ? addPackageMutation.isPending
            : updatePackageMutation.isPending
        }
      />
    </>
  );
});

export default Packages;
